import React from 'react';
import { Link } from 'react-router-dom';
import {
  ApartmentOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  LockOutlined,
  MailOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { getContactEmail } from '../../../../core/config/runtimeConfig';
import { route } from '../../../../constants/routes';
import LegalPage, { LegalNavigationItem, LegalSummaryItem } from './LegalPage';
import LegalSection from './LegalSection';

const UPDATED_AT = 'August 12, 2026';

const navigation: readonly LegalNavigationItem[] = [
  { id: 'agreement', label: 'Agreement', icon: <FileDoneOutlined /> },
  { id: 'service', label: 'ATS service', icon: <ApartmentOutlined /> },
  { id: 'accounts', label: 'Accounts & security', icon: <LockOutlined /> },
  { id: 'organizations', label: 'Organization duties', icon: <TeamOutlined /> },
  { id: 'acceptable-use', label: 'Acceptable use', icon: <StopOutlined /> },
  { id: 'candidate-data', label: 'Candidate data', icon: <FileTextOutlined /> },
  { id: 'ai', label: 'AI-assisted tools', icon: <RobotOutlined /> },
  { id: 'disclaimers', label: 'Disclaimers', icon: <WarningOutlined /> },
  { id: 'law', label: 'Governing law', icon: <AuditOutlined /> },
  { id: 'contact', label: 'Contact', icon: <MailOutlined /> },
] as const;

const summaryItems: readonly LegalSummaryItem[] = [
  {
    title: 'Use the ATS for legitimate hiring',
    description:
      'Accounts, candidate records, and communications must support authorized recruiting work.',
    icon: <SafetyCertificateOutlined />,
  },
  {
    title: 'Protect candidate information',
    description:
      'Organizations decide who may access data and must follow employment and privacy laws.',
    icon: <LockOutlined />,
  },
  {
    title: 'Keep people in the decision',
    description:
      'Automated suggestions require review and must not replace lawful human judgment.',
    icon: <RobotOutlined />,
  },
] as const;

type TermsAndConditionsProps = {
  embedded?: boolean;
};

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  embedded = false,
}) => {
  const contactEmail = getContactEmail();

  return (
    <LegalPage
      embedded={embedded}
      title="Terms and Conditions"
      description="The rules for responsible use of the Jobs & Opportunities ATS."
      updatedAt={UPDATED_AT}
      icon={<FileDoneOutlined />}
      navigation={navigation}
      summaryTitle="The short version"
      summaryItems={summaryItems}
    >
      <LegalSection id="agreement" number="01" title="Agreement to these terms">
        <p>
          These Terms and Conditions govern access to the Jobs &amp;
          Opportunities applicant tracking system, including organization
          workspaces, user accounts, job postings, candidate records,
          applications, messages, interviews, reports, and assisted recruiting
          features. By using the ATS, you agree to these terms and our{' '}
          <Link to={embedded ? route('/privacy') : '/privacy'}>
            Privacy Policy
          </Link>
          .
        </p>
        <p>
          If you use the ATS for an organization, you represent that you are
          authorized to accept these terms and use the service on its behalf. If
          you do not agree, do not access the ATS.
        </p>
      </LegalSection>

      <LegalSection id="service" number="02" title="The ATS service">
        <p>
          The ATS helps authorized recruiting teams manage jobs, candidates,
          applications, hiring stages, files, communications, interviews, and
          related reporting. Available features may depend on role, organization
          configuration, location, or third-party availability.
        </p>
        <p>
          We may improve, replace, limit, or discontinue features. We will use
          reasonable efforts to provide notice when a material change affects
          normal use of the service.
        </p>
      </LegalSection>

      <div className="ats-legal-responsibilities">
        <LegalSection id="accounts" title="Account holders">
          <ul className="ats-legal-check-list">
            <li>
              <CheckCircleOutlined /> Provide accurate account and profile
              information.
            </li>
            <li>
              <CheckCircleOutlined /> Keep credentials and verification codes
              confidential.
            </li>
            <li>
              <CheckCircleOutlined /> Use available security controls, including
              2FA where appropriate.
            </li>
            <li>
              <CheckCircleOutlined /> Report suspected unauthorized access
              promptly.
            </li>
          </ul>
        </LegalSection>

        <LegalSection id="organizations" title="Organizations">
          <ul className="ats-legal-check-list">
            <li>
              <CheckCircleOutlined /> Assign access only to authorized
              recruiting personnel.
            </li>
            <li>
              <CheckCircleOutlined /> Maintain accurate, lawful jobs and hiring
              workflows.
            </li>
            <li>
              <CheckCircleOutlined /> Establish retention and candidate-request
              procedures.
            </li>
            <li>
              <CheckCircleOutlined /> Train users to handle candidate
              information responsibly.
            </li>
          </ul>
        </LegalSection>
      </div>

      <LegalSection
        id="acceptable-use"
        title="Acceptable use"
        className="ats-legal-callout ats-legal-callout--warning"
      >
        <p>You must not:</p>
        <ul>
          <li>
            Use the ATS for unlawful, deceptive, discriminatory, or fraudulent
            hiring.
          </li>
          <li>Access a workspace, record, or file without authorization.</li>
          <li>
            Sell, scrape, or repurpose candidate data for unrelated marketing.
          </li>
          <li>
            Upload malware or content that violates law or third-party rights.
          </li>
          <li>
            Probe security, bypass access controls, or disrupt the service.
          </li>
          <li>
            Misrepresent automated output as verified fact or a final decision.
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        id="candidate-data"
        title="Candidate data and communications"
      >
        <p>
          Organizations are responsible for having an appropriate legal basis to
          collect, upload, use, share, and retain candidate information. They
          must provide required privacy notices, honor applicable rights, and
          limit access to people with a legitimate hiring need.
        </p>
        <p>
          Messages, clarification requests, interview invitations, and secure
          candidate links must be professional, relevant, and sent only for an
          authorized recruiting purpose. Users must verify recipients before
          sending confidential information.
        </p>
      </LegalSection>

      <LegalSection id="content" title="Content and platform rights">
        <p>
          Organizations and users keep ownership of content they submit. They
          grant us a limited, worldwide, non-exclusive license to host, process,
          reproduce, transmit, and display that content only as needed to
          operate, secure, support, and improve the ATS.
        </p>
        <p>
          You represent that you have the rights and permissions needed for the
          content you submit. The ATS software, brand, interface, and original
          platform materials remain owned by us or our licensors.
        </p>
      </LegalSection>

      <LegalSection id="ai" title="AI-assisted tools">
        <div className="ats-legal-callout ats-legal-callout--info">
          <RobotOutlined aria-hidden="true" />
          <div>
            <h3>Review every material output</h3>
            <p>
              Candidate summaries, matching evidence, skill suggestions, drafts,
              and similar outputs may contain errors or omit context. Users must
              review them before use and remain responsible for all employment
              decisions and communications.
            </p>
          </div>
        </div>
        <p>
          Assisted features may not be used as the sole basis for a decision
          that produces legal or similarly significant effects. Organizations
          must evaluate bias, relevance, accessibility, and applicable
          employment requirements before relying on an output.
        </p>
      </LegalSection>

      <LegalSection id="third-parties" title="Third-party services">
        <p>
          The ATS may integrate with authentication, email, messaging, calendar,
          storage, analytics, or other services. Third-party terms and privacy
          practices apply to those services. We are not responsible for
          third-party systems outside our control.
        </p>
      </LegalSection>

      <LegalSection
        id="disclaimers"
        number="03"
        title="Availability and disclaimers"
      >
        <p>
          The ATS supports recruiting operations but does not guarantee a
          successful hire, candidate response, complete record, or uninterrupted
          availability. Users must verify important candidate, job, interview,
          and compliance information before acting on it.
        </p>
        <p>
          To the extent permitted by law, the service is provided “as is” and
          “as available,” without warranties of merchantability, fitness for a
          particular purpose, non-infringement, or uninterrupted operation.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="Limitation of liability">
        <p>
          To the fullest extent permitted by applicable law, Jobs &amp;
          Opportunities and its operators will not be liable for indirect,
          incidental, special, consequential, or punitive damages, lost profits,
          loss of data, or employment decisions arising from use of the ATS.
          Nothing excludes liability that cannot legally be excluded.
        </p>
      </LegalSection>

      <LegalSection id="suspension" title="Suspension and termination">
        <p>
          We may restrict access, remove content, or suspend an account when
          reasonably necessary to protect people or data, enforce these terms,
          address nonpayment or abuse, comply with law, or maintain security.
          Organizations remain responsible for exporting or retaining records as
          required before account closure.
        </p>
      </LegalSection>

      <LegalSection id="law" title="Governing law and disputes">
        <p>
          These terms are governed by the laws applicable to the organization
          operating Jobs &amp; Opportunities, without overriding mandatory
          rights that apply to a user or candidate. Disputes will be submitted
          to the competent courts unless applicable law requires another forum.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to these terms">
        <p>
          We may update these terms as the ATS or legal requirements change. The
          date above identifies the latest version. Material changes will
          receive additional notice where required. Continued use after the
          effective date constitutes acceptance where permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact us">
        <p>
          Account and service questions may be sent to your organization
          administrator or the support channel supplied with your account.
          Questions about these terms may be sent
          {contactEmail ? (
            <>
              {' '}
              to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </>
          ) : (
            <>
              {' '}
              through the platform support contact provided by your
              organization.
            </>
          )}
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default TermsAndConditions;
